/**
 * Core-plugin extension seams — what the connector has actually **observed**, not what it assumes.
 *
 * The connector integrates with the public Guard Dog plugin through a small set of documented hooks
 * (ADR-0014). If the core build a site runs does not expose them, the connector still enrolls,
 * heartbeats and reports inventory — and every one of those integrations is silently dead. On
 * 2026-08-03 that was true of the entire fleet, because WordPress.org served a build with none of
 * the seams and nothing could tell the difference.
 *
 * ## Why this is observation, not inference
 *
 * Two tempting shortcuts do not work:
 *
 * 1. **`has_filter( … )` on the connector side.** The connector registers these filters *itself*, so
 *    the check is true whether or not core ever applies them. It would report every site healthy,
 *    including the broken ones — worse than no check, because it manufactures confidence.
 * 2. **A version → capability map on the platform side.** Core `1.9.50` shipped from outside the
 *    branch that carries the seams, so its version number says nothing about what it exposes. A map
 *    would have been wrong about precisely the build that mattered.
 *
 * So a seam counts as present only when the connector has seen it *fire*.
 *
 * ## The honest limit
 *
 * "Never observed" is **not** "absent". Some seams only fire on a failed login or a reputation
 * check, so a quiet site legitimately reports nothing. This carries the same ambiguity as a quiet
 * heartbeat, and consumers must not resolve it by guessing — see `SEAM_STATES`.
 */

/**
 * The seams the connector consumes, as a closed set.
 *
 * Closed so the console can render every one of them, including those a site has never reported —
 * a seam missing from a payload has to be distinguishable from a seam the connector does not know
 * about, and only a fixed list makes that possible.
 */
export const CORE_SEAMS = [
  'guard_dog_activity_logged',
  'guard_dog_activity_log_annotations',
  'guard_dog_ip_reputation_verdict',
  'guard_dog_lla_pre_auth_friction',
  'guard_dog_password_rejected',
] as const;

export type CoreSeam = (typeof CORE_SEAMS)[number];

/** What the platform can honestly say about one seam. */
export const SEAM_STATES = [
  /** The connector has seen this fire. The integration is live — the strongest claim available. */
  'observed',
  /**
   * Core declares its seam set and this one is not in it. The integration cannot work on this build,
   * and — unlike `not_observed` — that is a fact rather than an absence of evidence.
   *
   * Only reachable when core is new enough to declare (#231 option B). A build that does not declare
   * can never produce this state, which is the point: silence stays silence.
   */
  'absent',
  /**
   * Never seen firing, and core has not ruled it out. **This is not "absent".** The seam may be
   * missing from the core build, or the site may simply not have exercised it — a `password_rejected`
   * seam needs a failed login. Where core does not declare, these stay indistinguishable, and saying
   * otherwise would repeat the `connector_absent` mistake of asserting a cause we cannot see.
   */
  'not_observed',
  /** The site has never reported seam data at all — an older connector, or one that has not checked in. */
  'unreported',
] as const;

export type SeamState = (typeof SEAM_STATES)[number];

/** One seam's most recent observation, as reported by the connector. */
export interface SeamObservation {
  seam: CoreSeam;
  /** ISO-8601 of the most recent firing, or `null` if the connector has never seen it fire. */
  lastObservedAt: string | null;
}

/** What the platform holds for a site: the connector's latest snapshot, plus when it arrived. */
export interface SiteSeamReport {
  observations: SeamObservation[];
  /** When the connector last sent a seam report. Absent ⇒ it never has. */
  reportedAt?: string;
  /**
   * The seam set **core declared for itself** (#231 option B), reported verbatim by the connector.
   *
   * `undefined` means core did not declare — an older build, or one from outside the line that
   * carries the registry. It does **not** mean "no seams": the difference between "core says it has
   * none" and "core cannot say" is the whole reason this is optional rather than defaulting to `[]`.
   *
   * The connector may not synthesise this. It reads what core exposes and passes it on, or sends
   * nothing — inverting the trap that makes `has_filter()` useless here, since the connector cannot
   * accidentally satisfy a claim only core can make.
   */
  declaredSeams?: CoreSeam[];
}

/**
 * Resolve one seam's state for display.
 *
 * Four-valued, and deliberately refuses to collapse `not_observed` into "broken".
 *
 * **Observation outranks declaration.** If the connector has seen a seam fire, it is live no matter
 * what core's registry says — evidence beats a claim, and a stale or wrong registry must never
 * demote something demonstrably working. The reverse pairing (declared, never seen) stays
 * `not_observed`, because a declaration is not proof a site has exercised it.
 */
export function seamState(report: SiteSeamReport | undefined, seam: CoreSeam): SeamState {
  if (!report?.reportedAt) return 'unreported';
  const found = report.observations.find((o) => o.seam === seam);
  if (found?.lastObservedAt) return 'observed';
  if (report.declaredSeams && !report.declaredSeams.includes(seam)) return 'absent';
  return 'not_observed';
}

/**
 * Seams observed firing that core does not admit to exposing.
 *
 * The cross-check that keeps both halves of #231 worth having: option B reports what core *claims*,
 * option A what actually *fired*. Disagreement means the registry is wrong — core gained or renamed
 * a seam without updating its own declaration — and that is worth surfacing, because every later
 * answer derived from the registry inherits the error.
 *
 * Empty whenever core has not declared: with no claim there is nothing to contradict.
 */
export function seamDeclarationConflicts(report: SiteSeamReport | undefined): CoreSeam[] {
  if (!report?.declaredSeams) return [];
  const declared = new Set<string>(report.declaredSeams);
  return report.observations
    .filter((o) => o.lastObservedAt && !declared.has(o.seam))
    .map((o) => o.seam);
}

/**
 * Keep only known seam names from a core declaration, or `undefined` when core did not declare.
 *
 * `undefined` and `[]` are different answers and both are legitimate, so the absent case is
 * preserved rather than normalised away — a non-array input means "no claim", never "claims none".
 */
export function sanitizeDeclaredSeams(input: unknown): CoreSeam[] | undefined {
  if (!Array.isArray(input)) return undefined;
  const known = new Set<string>(CORE_SEAMS);
  return [...new Set(input.filter((v): v is CoreSeam => typeof v === 'string' && known.has(v)))];
}

/** Keep only well-formed observations for seams we actually know about. */
export function sanitizeSeamObservations(input: unknown): SeamObservation[] {
  if (!Array.isArray(input)) return [];
  const known = new Set<string>(CORE_SEAMS);
  const seen = new Set<string>();
  const out: SeamObservation[] = [];
  for (const raw of input) {
    if (!raw || typeof raw !== 'object') continue;
    const { seam, lastObservedAt } = raw as { seam?: unknown; lastObservedAt?: unknown };
    if (typeof seam !== 'string' || !known.has(seam) || seen.has(seam)) continue;
    const ts =
      typeof lastObservedAt === 'string' && Number.isFinite(Date.parse(lastObservedAt))
        ? lastObservedAt
        : null;
    seen.add(seam);
    out.push({ seam: seam as CoreSeam, lastObservedAt: ts });
  }
  return out;
}
